/**
 * Prioritization Inversion Detector
 *
 * A prioritization inversion exists when item A is ranked higher than item B
 * in a ranked set, but item A has a hard dependency on item B.
 *
 * This is a prioritization-stage concept — it is detected and acknowledged
 * before a Priority List can be published as a Plan of Record. It is distinct
 * from Rank–Sequence Divergence, which is a planning-stage concept (Build
 * Contract §4.4) governing when execution order differs from rank order.
 *
 * The system never re-ranks to resolve inversions. The human judgment that
 * produced the rank is preserved; acknowledgment creates a durable record
 * of why the inversion was accepted.
 *
 * Canon: DOCS_V4/00-canon/01-terminology-and-glossary_v3.md
 *        "Prioritization Inversion"
 */

import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';
import { isHardSequenceEdge, normalizeSequencePair } from './sequenceRelationships';

// ============================================================================
// Types
// ============================================================================

export interface PrioritizationInversion {
  /** The item ranked higher that depends on lowerRankedItemId. */
  higherRankedItemId: string;
  /** The item ranked lower that is a hard dependency of higherRankedItemId. */
  lowerRankedItemId: string;
  /** 1-based rank of the higher-ranked item (smaller number = higher priority). */
  higherRank: number;
  /** 1-based rank of the dependency item (larger number = lower priority). */
  lowerRank: number;
  /** ID of the hard dependency edge that creates this inversion, if available. */
  dependencyEdgeId?: string;
}

export interface InversionAcknowledgment {
  id: string;
  workspaceId: string;
  prioritySetId: string;
  higherRankedItemId: string;
  lowerRankedItemId: string;
  higherRank: number;
  lowerRank: number;
  dependencyEdgeId?: string;
  acknowledgedBy: string;
  acknowledgedAt: Date;
  rationale: string;
  createdAt: Date;
}

export interface InversionAcknowledgmentRow {
  id: string;
  workspace_id: string;
  priority_set_id: string;
  higher_ranked_item_id: string;
  lower_ranked_item_id: string;
  higher_rank: number;
  lower_rank: number;
  dependency_edge_id?: string | null;
  acknowledged_by: string;
  acknowledged_at: string;
  rationale: string;
  created_at: string;
}

/** Unique key for matching inversions to their acknowledgments. */
export function inversionKey(higherItemId: string, lowerItemId: string): string {
  return `${higherItemId}::${lowerItemId}`;
}

// ============================================================================
// Detection
// ============================================================================

/**
 * Detect all prioritization inversions in a ranked item set.
 *
 * An inversion is: item A at rank N depends (hard) on item B at rank M, where M > N
 * (item B is lower priority than item A, but item A needs it).
 *
 * @param rankedItemIds - Ordered array of item IDs. Index 0 = rank 1 (highest priority).
 * @param edges - All active item relationship edges in the workspace.
 * @returns Inversions sorted by the higher-ranked item's position.
 */
export function detectPrioritizationInversions(
  rankedItemIds: string[],
  edges: ItemRelationshipEdge[],
): PrioritizationInversion[] {
  if (rankedItemIds.length === 0) return [];

  // Build rank lookup: itemId → 1-based rank position
  const rankByItemId = new Map<string, number>();
  rankedItemIds.forEach((id, index) => {
    rankByItemId.set(id, index + 1);
  });

  const rankedItemSet = new Set(rankedItemIds);
  const inversions: PrioritizationInversion[] = [];
  const seenPairs = new Set<string>();

  // Hard sequence edges (dependency + prerequisite), normalized to predecessor → dependent.
  // Inversion: dependent ranked higher (smaller rank number) than its predecessor.
  for (const edge of edges) {
    if (edge.status === 'dismissed') continue;
    if (!isHardSequenceEdge(edge)) continue;
    const pair = normalizeSequencePair(edge);
    if (!pair) continue;
    if (!rankedItemSet.has(pair.predecessorItemId) || !rankedItemSet.has(pair.dependentItemId)) {
      continue;
    }

    const dependentRank = rankByItemId.get(pair.dependentItemId)!;
    const predecessorRank = rankByItemId.get(pair.predecessorItemId)!;

    if (dependentRank < predecessorRank) {
      const key = `${pair.dependentItemId}::${pair.predecessorItemId}`;
      if (seenPairs.has(key)) continue;
      seenPairs.add(key);
      inversions.push({
        higherRankedItemId: pair.dependentItemId,
        lowerRankedItemId: pair.predecessorItemId,
        higherRank: dependentRank,
        lowerRank: predecessorRank,
        dependencyEdgeId: pair.edgeId,
      });
    }
  }

  return inversions.sort((a, b) => a.higherRank - b.higherRank);
}

// ============================================================================
// Acknowledgment helpers
// ============================================================================

export function rowToInversionAcknowledgment(row: InversionAcknowledgmentRow): InversionAcknowledgment {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    prioritySetId: row.priority_set_id,
    higherRankedItemId: row.higher_ranked_item_id,
    lowerRankedItemId: row.lower_ranked_item_id,
    higherRank: row.higher_rank,
    lowerRank: row.lower_rank,
    dependencyEdgeId: row.dependency_edge_id ?? undefined,
    acknowledgedBy: row.acknowledged_by,
    acknowledgedAt: new Date(row.acknowledged_at),
    rationale: row.rationale,
    createdAt: new Date(row.created_at),
  };
}

/**
 * Given a list of detected inversions and existing acknowledgments,
 * return the subset of inversions that have not yet been acknowledged.
 */
export function getUnacknowledgedInversions(
  inversions: PrioritizationInversion[],
  acknowledgments: InversionAcknowledgment[],
): PrioritizationInversion[] {
  const ackedKeys = new Set(
    acknowledgments.map((a) => inversionKey(a.higherRankedItemId, a.lowerRankedItemId)),
  );
  return inversions.filter(
    (inv) => !ackedKeys.has(inversionKey(inv.higherRankedItemId, inv.lowerRankedItemId)),
  );
}

/**
 * Acknowledgments by the item ranked above its dependency: the row the
 * acknowledged-inversion mark belongs on (InversionMark.tsx; owner,
 * 2026-10-30: the exception is drawn beside the thing it qualifies).
 */
export function acknowledgedInversionsByHigherItem(
  acknowledgments: readonly InversionAcknowledgment[],
): Map<string, InversionAcknowledgment[]> {
  const out = new Map<string, InversionAcknowledgment[]>();
  for (const a of acknowledgments) {
    const list = out.get(a.higherRankedItemId) ?? [];
    list.push(a);
    out.set(a.higherRankedItemId, list);
  }
  return out;
}
